<template>
  <div class="desktop-wrapper">
    <desktop-header
      @locale="setLocale"
      @print="print"
      :calname="calInfo.name"
      :schoolname="schoolName">
    </desktop-header>
    <div class="wrapper">
      <!-- Week Selector -->
      <week-selector
        :startDate="calInfo.start_date"
        :endDate="calInfo.end_date"
        :date="date"
        @changed="setDate">
      </week-selector>
      <div id="printCalendar">
        <h2 class="only-print print-calendar-name">{{ calInfo.name }}</h2>
        <!-- DCalendar -->
        <div class="box cal-container">
          <div class="cal-container__header">
            <div class="cal-container__header--left">
              <h3>{{ showDate }}</h3>
            </div>
            <div class="cal-container__header--right">
              <div class="cal-toggle no-print" @click="isCalNoBgColors = !isCalNoBgColors">
                <label class="cal-toggle__label">{{ $t('vue2.public_calendar.black_white') }}</label>
                <AppToggle :allowChanges="false" :propOn="isCalNoBgColors"></AppToggle>
              </div>
              <button class="do-bttn no-print"
                      @click="setToday"
                      :class="{'bttn--disabled': !isNotThisWeek}">
                {{ $t('repitition.today') }}
              </button>
              <button class="do-bttn icon-arrow-left4 no-print" @click="prevWeek"></button>
              <button class="do-bttn icon-arrow-right4 no-print" @click="nextWeek"></button>
            </div>
          </div>
          <div class="d-calendar__calendar__container"
               :class="{'d-calendar__calendar__container--print-mode': isCalendarInPrintMode}">
            <DCalendar
              :key="activeCalendarDate + '_' + locale"
              :isEditable="false"
              :isCalendarInPrintMode="isCalendarInPrintMode"
              :isShowWeekends="isShowWeekends"
              :isCalNoBgColors="isCalNoBgColors"
              :startTime="startTime"
              :endTime="endTime"
              :date="activeCalendarDate"
              :events="allEvents"
              :calendarId="calId"
              :calendarName="calInfo.name || ''"
              :isEventsClickable="true"
              :isLoadingEvents="isLoading"
              :localeOverride="locale"
              @onEventClick="showEvent">
            </DCalendar>
          </div>
        </div>
        <!-- Weekplan -->
        <weekplan
          :locale="locale"
          :calId="calId"
          :date="date"
          :events="allEvents"
          :calview="'agendaWeek'">
        </weekplan>
      </div>
    </div>
  </div>
</template>

<script>
  import desktopHeader from './Header.vue'
  import weekSelector from '../../datepicker/WeekSelecter.vue'
  import weekplan from './weekplan/WeekPlan.vue'
  import DCalendar from '../../DCalendar/DCalendar'
  import AppToggle from '../../common/AppToggle'
  import moment from 'moment'
  import docendo from '../../../docendo'
  import config from '../../../config'

  export default {
    components: {
      desktopHeader,
      weekSelector,
      weekplan,
      DCalendar,
      AppToggle
    },
    props: {
      calId: {},
      schoolName: {},
      i18n: {}
    },
    data () {
      return {
        allEvents: [],
        date: config.now,
        today: config.now,
        locale: window.USER.locale,
        calInfo: '',
        isLoading: false,
        isShowWeekends: false,
        isCalNoBgColors: false,
        startTime: '06:00:00',
        endTime: '17:00:00',
        intervalId: null,
        isCalendarInPrintMode: false
      }
    },
    computed: {
      activeCalendarDate () {
        return moment(this.date).locale('en').format('YYYY-MM-DD')
      },
      weekStartDate () {
        return moment(this.date).isoWeekday(1).locale('en').format('YYYY-MM-DD 00:00')
      },
      weekEndDate () {
        return moment(this.date).isoWeekday(7).locale('en').format('YYYY-MM-DD 23:59')
      },
      isNotToday () {
        return moment(this.date).locale('en').format('YYYY-MM-DD') !== moment(this.today).locale('en').format('YYYY-MM-DD')
      },
      isNotThisWeek () {
        return moment(this.date).isoWeek() !== moment(this.today).isoWeek()
      },
      showDate () {
        try {
          if (this.locale === 'nn' || this.locale === 'nb') {
            return moment(this.date).locale(this.locale).format('MMMM YYYY')
          }
          return moment(this.date)._d.toLocaleDateString(this.locale, {month: 'long', year: 'numeric'})
        } catch (e) {
          return moment(this.date).format('LL')
        }
      }
    },
    watch: {
      calInfo () {
        if (this.calInfo && this.calInfo.view_state > -1) {
          var state = this.calInfo.view_state * 1
          if (state === 0) {
            this.startTime = '06:00:00'
            this.endTime = '17:00:00'
            this.isShowWeekends = false
          } else if (state === 1) {
            this.startTime = '06:00:00'
            this.endTime = '17:00:00'
            this.isShowWeekends = true
          } else {
            this.startTime = '00:00:00'
            this.endTime = '23:00:00'
            this.isShowWeekends = true
          }
        }
      }
    },
    methods: {
      getSchoolInfo () {
        docendo.calendar(this.calId, (data) => {
          this.calInfo = data
        })
      },

      getLessons () {
        this.isLoading = true
        docendo.publicEventsFromCalendar(this.calId, this.weekStartDate, this.weekEndDate, (data) => {
          // Svaret kan være en JSON-streng, så parse det hvis nødvendigt
          var events = data
          if (typeof data === 'string') {
            try {
              events = JSON.parse(data)
            } catch (e) {
              events = []
            }
          }
          var parsed = Array.isArray(events) ? events : []
          var calId = this.calId * 1
          for (var i = 0; i < parsed.length; i++) {
            var ev = parsed[i]
            // Fjern advarselstrekanter fra events i den offentlige visning
            delete ev.collision
            delete ev.away
            // Map calendars-array til flade group/teacher felter
            var calendars = ev.calendars || []
            var groups = []
            var teachers = []
            for (var j = 0; j < calendars.length; j++) {
              var cal = calendars[j]
              if (cal.calendar_type === 'klass' && cal.calendar_id !== calId) {
                groups.push(cal.name)
              } else if (cal.calendar_type === 'user') {
                teachers.push(cal.name)
              }
            }
            ev.group = groups.join(', ')
            ev.teacher = teachers.join(', ')
            ev.room = ev.room || ''
            ev.canceled = ev.canceled || false
            ev.away = false
            ev.has_substitute = ev.has_substitute || false
            // Beregn duration i minutter fra start/end
            ev.duration = moment(ev.end).diff(moment(ev.start), 'minutes')
            // Sæt has_notes så ugeplanen kan filtrere events med noter
            var desc = (ev.description || '').replace(/<[^>]*>/g, '').trim()
            var goal = (ev.learning_goal || '').replace(/<[^>]*>/g, '').trim()
            var studentNotes = (ev.notes_for_students || '').replace(/<[^>]*>/g, '').trim()
            var teacherNotes = (ev.teacher_notes || '').replace(/<[^>]*>/g, '').trim()
            ev.has_notes = !!(desc || goal || studentNotes || teacherNotes)
          }
          this.allEvents = parsed
          this.isLoading = false
        }, () => {
          this.isLoading = false
        })
      },
      setDate (date) {
        this.date = date
        this.getLessons()
      },
      nextWeek () {
        var date = moment(this.date).add(7, 'days')
        this.setDate(date)
      },
      prevWeek () {
        var date = moment(this.date).subtract(7, 'days')
        this.setDate(date)
      },
      setToday () {
        this.setDate(this.today)
      },
      showEvent (event) {
        // DCalendar emitter event som { obj: {...} } – map til det format ShowEvent forventer
        var eventData = event.obj || event
        var calendars = eventData.calendars || []
        var groups = []
        var teachers = []
        var rooms = []
        for (var i = 0; i < calendars.length; i++) {
          var cal = calendars[i]
          if (cal.calendar_type === 'klass' && cal.calendar_id !== this.calId) {
            groups.push(cal.name)
          } else if (cal.calendar_type === 'user') {
            teachers.push(cal.name)
          } else if (cal.calendar_type === 'room') {
            rooms.push(cal.name)
          }
        }
        var duration = moment(eventData.end).diff(moment(eventData.start), 'minutes')
        var mappedEvent = {
          title: eventData.title,
          color: eventData.color,
          start: eventData.start,
          end: eventData.end,
          duration: duration,
          group: groups.join(', '),
          teacher: teachers.join(', '),
          room: rooms.join(', '),
          description: eventData.description || '',
          learning_goal: eventData.learning_goal || '',
          notes_for_students: eventData.notes_for_students || '',
          canceled: eventData.canceled || false,
          away: eventData.away || false
        }
        this.$emit('eventClick', mappedEvent)
      },
      setLocale (val) {
        this.locale = val
        this.$emit('setLocale', val)
      },
      print () {
        const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0')
        if (!WinPrint) {
          return
        }
        this.isCalendarInPrintMode = true
        this.$nextTick(() => {
          setTimeout(() => {
            const weekplanEl = this.$el.querySelector('#printCalendar .weekplan')
            const hasNotes = this.$el.querySelector('#printCalendar .day_change_header') ||
              this.$el.querySelector('#printCalendar .week_note')
            if (weekplanEl && !hasNotes) {
              weekplanEl.classList.add('hide')
            }
            const printEl = document.getElementById('printCalendar')
            if (!printEl) {
              WinPrint.close()
              this.isCalendarInPrintMode = false
              return
            }
            const prtHtml = printEl.innerHTML
            let stylesHtml = ''
            for (const node of [...document.querySelectorAll('link[rel="stylesheet"][href*="/app/static/css/"], style')]) {
              stylesHtml += node.outerHTML
            }
            const title = (this.calInfo.name || 'Docendo').replace(/</g, '')
            WinPrint.document.write(`<!DOCTYPE html><html><head><title>${title}</title>${stylesHtml}<style>html,body{-webkit-print-color-adjust:exact;print-color-adjust:exact}</style></head><body>${prtHtml}</body></html>`)
            const afterPrint = () => {
              WinPrint.close()
              this.isCalendarInPrintMode = false
              if (weekplanEl) {
                weekplanEl.classList.remove('hide')
              }
            }
            WinPrint.onafterprint = afterPrint
            setTimeout(() => {
              WinPrint.document.close()
              WinPrint.focus()
              WinPrint.print()
            }, 1000)
          }, 500)
        })
      }
    },
    beforeMount () {
      if (window.location.href && window.location.href.indexOf('?update') > -1) {
        this.intervalId = setInterval(this.getLessons, 300000)
      }
    },
    mounted () {
      this.getSchoolInfo()
      this.getLessons()
      if (this.$helpers.getCookie('calendar_current_date_public') && this.$helpers.getCookie('calendar_current_date')) {
        this.setDate(this.$helpers.getCookie('calendar_current_date'))
      }
    },
    beforeDestroy () {
      if (this.intervalId) {
        clearInterval(this.intervalId)
      }
    }
  }
</script>

<style scoped>
  .desktop-wrapper {
    min-width: 600px;
  }

  .wrapper {
    max-width: 1440px;
    margin: 0 auto;
  }

  .cal-container {
    display: flex;
    flex-direction: column;
  }

  .cal-container__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  }

  .cal-container__header h3 {
    opacity: .54;
    text-transform: capitalize;
    font-size: 20px;
    font-weight: 400;
  }

  .cal-container__header--right {
    display: flex;
    align-items: center;
  }

  .cal-container__header--right > button {
    margin-left: 0.5rem;
  }

  .cal-toggle {
    display: flex;
    align-items: center;
    cursor: pointer;
    margin-right: 1rem;
    gap: 0.5rem;
    border: 1px solid #ccc;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .cal-toggle__label {
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
  }

  .d-calendar__calendar__container {
    position: relative;
    height: calc(100vh - 200px);
    min-height: 650px;
  }

  .d-calendar__calendar__container.d-calendar__calendar__container--print-mode {
    height: 650px;
  }

  .print-calendar-name {
    margin: 0 0 0.5rem;
    font-size: 1.25rem;
  }

  @media print {
    .wrapper {
      max-width: 100vw;
    }

    .box {
      padding: 0;
      border-radius: 0;
    }

    .d-calendar__calendar__container {
      height: 650px;
    }
  }
</style>



// WEBPACK FOOTER //
// DesktopView.vue?5364e25e